import { BadgeV3, ButtonV2, DataGrid, DataGridColumn, FilterV2, Progress, SearchV2, SortV2, Text, TooltipV2 } from '@cimplico/components';
import NavBreadcrumbs from '../components/nav-breadcrumbs/NavBreadcrumbs';
import PageTitle from '../components/PageTitle';
import { useCalculations } from '../queries/use-calculations';
import { Link, useNavigate } from 'react-router-dom';
import EmptyStateNew from '../components/empty-state/EmptyStateNew';
import { ROW_SIZES } from '../constants';
import { WorksheetSchema } from '../types/custom-worksheet-v2';
import { getFieldCount } from '../features/worksheet-v2/custom-worksheet-v2/store/utils';
import { calcRelativeTime, formatDateTime } from '../utilities/formatting';
import { TagIcon } from '@heroicons/react/20/solid';
import GrowHeightContainer from '../components/GrowHeightContainer';
import { useState } from 'react';

const columns: DataGridColumn[] = [
    {
        header: 'Name',
        alignment: 'left',
        width: '2fr',
    },
    {
        header: 'Tags',
        alignment: 'left',
        width: '1fr',
    },
    {
        header: 'Progress',
        alignment: 'center',
        width: '1fr',
    },
    {
        header: 'Worksheets',
        alignment: 'center',
        width: '1fr',
    },
    {
        header: 'Last Updated',
        alignment: 'left',
        width: '1fr',
    },
];
const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'updatedAt', label: 'Last Updated' },
    { value: 'worksheets', label: 'Worksheets' },
];
function CalculationsPage(): JSX.Element {
    const [sort, setSort] = useState('');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(ROW_SIZES[0]);
    const calculationsQuery = useCalculations({ search: search, sort: sort, filters: filter, page: page, limit: limit });
    const calculations = calculationsQuery.data?.calculations ?? [];
    const totalItems = calculationsQuery.data?.totalItems ?? 0;

    const navigate = useNavigate();
    if (calculationsQuery.isLoading) {
        return <></>;
    }
    function redirectToNewCalculation(): void {
        navigate('new');
    }
    return (
        <div>
            <section>
                <div className="flex flex-col justify-between flex-wrap gap-2 mb-4">
                    <div className="mb-[-8px]">
                        <NavBreadcrumbs />
                    </div>
                    <div className="flex justify-between">
                        <PageTitle title="Calculations" />
                        <ButtonV2 size="sm" label="New Calculation" onClick={redirectToNewCalculation} />
                    </div>
                </div>
                <div className="border-b h-4" />
            </section>
            <section>
                <GrowHeightContainer className="flex flex-wrap items-center gap-2 mt-4">
                    <FilterV2
                        onChange={({ queryString }): void => {
                            setFilter(queryString);
                        }}
                    >
                        <FilterV2.DateFilter id="updatedAt" label="Last Updated" />
                        <FilterV2.NumericFilter id="worksheets" label="Worksheets" />
                    </FilterV2>
                    <SearchV2 onChange={setSearch} placeholder="Search..." />
                    <SortV2
                        options={sortOptions}
                        defaultValue="name"
                        defaultDirection="asc"
                        onChange={({ queryString }): void => {
                            setSort(queryString);
                        }}
                    />
                </GrowHeightContainer>
            </section>
            <div className="w-full mt-12">
                <DataGrid
                    columns={columns}
                    rowSizes={ROW_SIZES}
                    totalRows={totalItems}
                    isLoading={calculationsQuery.isLoading}
                    onPageChange={setPage}
                    onRowSizeChange={setLimit}
                    emptyMessage={<EmptyStateNew title={'No Calculations found'} />}
                >
                    {calculations.map((calculation) => (
                        <DataGrid.Row key={calculation.id} id={calculation.id}>
                            {/* Name + Description */}
                            <div className="flex-1 ">
                                <Link to={calculation.id} className="text-sm font-medium text-indigo-600 dark:text-indigo-400 break-words">
                                    {calculation.name}
                                </Link>
                            </div>
                            {/* Tags */}
                            <div className="flex items-center gap-1">
                                <Tags tags={calculation.tags} />
                            </div>
                            {/* Progress */}
                            <Progress type="circle" percent={getCalculationProgress(calculation.schema)} showProgressLabel size="small" />
                            {/* Worksheet */}
                            <span className="">
                                <TooltipV2 message={calculation.worksheets.map((ws) => ws.name)}>
                                    <label className="text-sm text-gray-700">{calculation.worksheets.length}</label>
                                </TooltipV2>
                            </span>
                            {/* Updated At */}
                            <TooltipV2 message={formatDateTime(calculation.updatedAt)}>
                                <p className="text-sm text-gray-700">{calcRelativeTime(calculation.updatedAt)}</p>
                            </TooltipV2>
                        </DataGrid.Row>
                    ))}
                </DataGrid>
            </div>
        </div>
    );
}
export default CalculationsPage;

function getCalculationProgress(schema: WorksheetSchema): number {
    const { total, completed } = getFieldCount(schema, true);
    return total === 0 ? 0 : Math.floor((completed / total) * 100);
}
type TagsProps = {
    tags: string[];
    maxLength?: number;
};

function Tags({ tags, maxLength = 1 }: TagsProps): JSX.Element {
    // Nothing to render
    if (tags.length === 0) {
        return <></>;
    }
    // Show all if 1 or 2 tags, when maxLength is the default value of 2.
    if (tags.length <= maxLength) {
        return (
            <>
                {tags.map((t) => (
                    <BadgeV3 key={t} label={t} color="green" leftIcon={<TagIcon />} />
                ))}
            </>
        );
    }
    // length of tags is more than maxLength prop: then show maxLength amount of tags , and rest in tooltip
    return (
        <>
            {tags.slice(0, maxLength).map((t) => (
                <BadgeV3 key={t} label={t} color="green" leftIcon={<TagIcon />} />
            ))}
            <TooltipV2 message={tags.slice(maxLength)}>
                <label className="text-sm text-gray-800">+{tags.length - maxLength}</label>
            </TooltipV2>
        </>
    );
}
